import React, { FC, PropsWithChildren } from 'react';
import CustomModal from '../../components-library/CustomModal';
import { MODAL_SIZES, REDUX_STORE_SLICES, SCREENS } from '../../services/exports/Constants';
import useAppMethods from '../../hooks/utility/useAppMethods';
import CloseCircleIcon from '../../assets/icons/CloseCircleIcon';
import { ButtonTypes, GenericModalProps } from '../../services/exports/Interfaces';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import CustomButton from '../../components-library/buttons/CustomButton';
import { useDispatch, useSelector } from 'react-redux';
import giftCardsBasketSlice from '../../store/slices/GiftCardBasketSlice';
import useRouter from '../../hooks/navigation/useRouter';
import { zodResolver } from '@hookform/resolvers/zod';
import { BuyGiftCardForm, GIFT_CARD_MAX_QUANTITY, createBuyGiftCardSchema } from './types';
import useAlert from '../../hooks/utility/useAlert';
import ConfirmationAlert from './ConfirmationAlert';
import useScreenType from '../../hooks/utility/useScreenType';
import classnames from 'classnames';
import CurrencyInput from 'react-currency-input-field';
import { StoreInterface } from '../../store/types';
import useGiftCardLimits from '../../features/gift-cards/hooks/useGiftCardLimits';
import ButtonFooter from '../../components-library/footers/ButtonFooter';
import PriceComparison from '../../components-library/menu/PriceComparison';
import Theme from '../../components-library/Theme';
import CounterButton from '../../components-library/buttons/CounterButton';
import useTheme from '../../hooks/ui/useTheme';
import { Routes } from '../../modules/mobile/exports/Enums';
import useMobileApp from '../../hooks/mobile-app/useMobileApp';
import useModalRouter from '../../modules/mobile/common/hooks/useModalRouter';

const ITEMS = ['gift_card', 'expiration', 'delivery'];

const BuyGiftCardModal: FC<Partial<GenericModalProps>> = () => {
  return (
    <Wrapper>
      <div className="space-y-md bg-white">
        <Header />
        <Content />
      </div>
    </Wrapper>
  );
};

const Wrapper = (props: PropsWithChildren) => {
  const { isDesktop } = useScreenType();
  const { isMobileApp } = useMobileApp();

  if (isMobileApp) {
    return props.children;
  }

  return (
    <CustomModal
      open
      position="center"
      zIndex={1300}
      size={MODAL_SIZES.XL}
      containerClassName={classnames(isDesktop && 'overflow-y-auto !w-[min(100%,900px)]')}
      fullScreen={!isDesktop}
    >
      {props.children}
    </CustomModal>
  );
};

const Header = () => {
  const { toggleBuyGiftCardModal } = useAppMethods();
  const { t } = useTranslation(null, { keyPrefix: 'Modals:BuyGiftCardModal.header' });
  const { isMobileApp } = useMobileApp();
  const { closeModal } = useModalRouter();
  const onClose = () => {
    if (!isMobileApp) {
      toggleBuyGiftCardModal({ openModal: false });

      return;
    }

    closeModal();
  };

  return (
    <div
      className={classnames('flex items-center p-md sticky top-0 bg-white z-[9999] w-full', {
        'justify-center': isMobileApp,
      })}
    >
      <h3 className="text-md">{t('title')}</h3>
      <button className="absolute right-md" onClick={onClose}>
        <CloseCircleIcon />
      </button>
    </div>
  );
};

const Content = () => {
  const { min, max, presets } = useGiftCardLimits();
  const form = useForm<BuyGiftCardForm>({
    resolver: zodResolver(createBuyGiftCardSchema(min / 100, max / 100)),
    defaultValues: {
      value: presets[Math.floor(presets.length / 2)],
      email: '',
      quantity: 1,
      custom_value: undefined,
    },
  });
  const dispatch = useDispatch();
  const { navigate } = useRouter();
  const { toggleBuyGiftCardModal } = useAppMethods();
  const { isMobileApp } = useMobileApp();
  const { closeModal } = useModalRouter();
  const alert = useAlert();
  const onCloseModal = () => {
    if (!isMobileApp) {
      toggleBuyGiftCardModal({ openModal: false });

      return;
    }

    closeModal();
  };

  const onConfirm = (data: BuyGiftCardForm): void => {
    alert.toggleAlert();

    const formattedData: BuyGiftCardForm = {
      ...data,
      value: data.custom_value * 100 || data.value,
    };

    delete formattedData.custom_value;

    dispatch(giftCardsBasketSlice.actions.addToBasket(formattedData));
    onCloseModal();
    navigate({
      web: { to: SCREENS.GIFT_CARDS_CHECKOUT },
      mobile: {
        path: Routes.GiftCardCheckout,
      },
    });
  };

  const onSubmit = form.handleSubmit((data): void => {
    alert.error({
      title: <ConfirmationAlert.Title {...data} />,
      description: <ConfirmationAlert.Description {...data} />,
      footer: <ConfirmationAlert.Footer onConfirm={() => onConfirm(data)} onCancel={() => alert.toggleAlert()} />,
    });
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={onSubmit} className="space-y-lg relative">
        <div className="p-md space-y-lg">
          <FirstRow />
          <BenefitsRow />
          <ValuesRow />
          <EmailRow />
        </div>
        <Footer />
      </form>
    </FormProvider>
  );
};

const FirstRow = () => {
  const { t } = useTranslation(null, { keyPrefix: 'Modals:BuyGiftCardModal.items' });
  const redemptionMode = useSelector(
    (state: StoreInterface) => state[REDUX_STORE_SLICES.INITIAL_DATA]?.franchise?.gift_cards_redemption_mode,
  );
  const getTranslation = (key: string) => {
    if (key !== 'gift_card') {
      return t(`${key}.label`);
    }

    return t(`${key}.${redemptionMode}`);
  };

  return (
    <div className="flex gap-md flex-col md:flex-row">
      {ITEMS.map((item) => (
        <div key={item} className="flex gap-xs">
          <Check className="text-brand-primary" />
          {getTranslation(item)}
        </div>
      ))}
    </div>
  );
};

const BenefitsRow = () => {
  const { t } = useTranslation(null, { keyPrefix: 'Modals:BuyGiftCardModal.items' });

  return (
    <div className="grid grid-cols-1 items-start md:grid-cols-3 gap-y-md">
      {ITEMS.map((item, index) => (
        <div key={item} className="grid grid-cols-[auto,1fr] gap-2 items-center">
          <span className="bg-brand-primary size-8 rounded-full flex items-center justify-center text-white text-base">
            {index + 1}
          </span>
          <h4 className="text-base">{t(`${item}.title`)}</h4>
          <p className="col-start-2 row-start-2">{t(`${item}.description`)}</p>
        </div>
      ))}
    </div>
  );
};

const ValuesRow = () => {
  const { formatCurrency } = useAppMethods();
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<BuyGiftCardForm>();
  const { t, i18n } = useTranslation(null, { keyPrefix: 'Modals:BuyGiftCardModal.form.value' });
  const selectedValue = watch('value');
  const currency = useSelector((state: StoreInterface) => state.initialData?.company?.currency);
  const { min, max, presets } = useGiftCardLimits();
  const customPlaceholder = t('custom.placeholder', { currency, min: min / 100, max: max / 100 });

  return (
    <div className="flex flex-col gap-md">
      <h3 className="text-base">{t('title')}</h3>
      <Controller
        name="value"
        control={control}
        render={({ field }) => (
          <ToggleButtonGroup
            exclusive
            value={field.value}
            onChange={(_, val) => {
              val !== null && field.onChange(val);
            }}
            className="gap-md flex-wrap justify-center sm:justify-start"
          >
            {presets.map((value) => (
              <ToggleButton key={value} value={value} className={radioBtnClassName}>
                {formatCurrency(value / 100)}
              </ToggleButton>
            ))}
            <ToggleButton key={0} value={0} className={radioBtnClassName}>
              {t('custom.label')}
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      />

      {selectedValue === 0 && (
        <Controller
          name="custom_value"
          shouldUnregister
          control={control}
          render={({ field }) => (
            <CurrencyInput
              value={field.value}
              onValueChange={field.onChange}
              customInput={TextField}
              placeholder={customPlaceholder}
              inputMode="numeric"
              className="w-80 [&_.Mui-focused_.MuiOutlinedInput-notchedOutline]:!border-brand-primary [&_.MuiOutlinedInput-notchedOutline]:hover:!border-brand-primary !mx-auto md:!mx-0"
              allowNegativeValue={false}
              disableGroupSeparators
              intlConfig={{
                locale: i18n.language,
                currency,
              }}
              {...{
                error: !!errors.value,
                helperText: !!errors.value && customPlaceholder,
              }}
            />
          )}
        />
      )}
    </div>
  );
};

const Footer = () => {
  const theme = useTheme();
  const { t } = useTranslation(null, { keyPrefix: 'Modals:BuyGiftCardModal.checkout_btn' });
  const { watch, setValue } = useFormContext<BuyGiftCardForm>();
  const [value, customValue, quantity] = watch(['value', 'custom_value', 'quantity']);
  const selectedValue = value / 100 || customValue || 0;
  const total = selectedValue * quantity;

  return (
    <ButtonFooter className="sticky bottom-0 space-y-8">
      <div className="lg:grid lg:grid-cols-12 flex gap-[10px] items-center">
        <div className="lg:col-span-4 shrink-none min-w-[100px]">
          <CounterButton
            classes={{
              container: 'h-[43px]',
              buttons: '!w-4',
            }}
            iconColor={theme.color.brand.text.secondary}
            count={quantity}
            max={GIFT_CARD_MAX_QUANTITY}
            increase={() => setValue('quantity', quantity + 1, { shouldValidate: true })}
            decrease={() => setValue('quantity', quantity - 1, { shouldValidate: true })}
          />
          {quantity >= GIFT_CARD_MAX_QUANTITY && (
            <p className="mt-2xs text-xs text-muted-foreground text-center" data-testid="gift-card-max-quantity">
              {t('max_quantity', { max: GIFT_CARD_MAX_QUANTITY })}
            </p>
          )}
        </div>
        <div className="lg:col-span-8 flex-1 shrink-0">
          <CustomButton
            title={
              <div className="flex items-center justify-between gap-1 w-full">
                <span className="text-brand-button-primary-text line-clamp-1 text-sm">{t('title')}</span>
                <PriceComparison
                  actualPrice={total}
                  originalPrice={total}
                  originalPriceStyle={{
                    fontSize: Theme.font.size.sm,
                    color: theme.buttons[ButtonTypes.primary].text,
                  }}
                  actualPriceStyle={{
                    fontSize: Theme.font.size.sm,
                    color: theme.buttons[ButtonTypes.primary].text,
                  }}
                />
              </div>
            }
            type="submit"
          />
        </div>
      </div>
    </ButtonFooter>
  );
};

const EmailRow = () => {
  const { t } = useTranslation(null, { keyPrefix: 'Modals:BuyGiftCardModal.form.email' });
  const {
    register,
    formState: { errors },
  } = useFormContext<BuyGiftCardForm>();

  return (
    <div className="flex flex-col gap-md">
      <h3 className="text-base">{t('title')}</h3>
      <TextField
        placeholder={t('placeholder')}
        className="[&_.Mui-focused_.MuiOutlinedInput-notchedOutline]:!border-brand-primary [&_.MuiOutlinedInput-notchedOutline]:hover:!border-brand-primary"
        fullWidth
        inputMode="email"
        {...register('email')}
        error={!!errors.email}
        helperText={!!errors.email && t('error')}
      />
    </div>
  );
};

const radioBtnClassName =
  '!rounded-lg w-32 h-16 !text-black !border-brand-border [&.Mui-selected]:!bg-brand-primary/10 [&.Mui-selected]:!border-brand-primary [&.Mui-selected]:!border-lg hover:!border-brand-primary/50';

export default BuyGiftCardModal;
